import { ECompanyType, EFinanceLianStatus, ELianLianStepKey, EPayeeIdType, EUserType, IAgentMergeInfo, ICompany, ICompanyMergeInfo, IFinanceLianAccOpening, IFinanceLianDetail, IFinanceLianPersonal, ILianDetail, IServerMergeInfo } from '@core/main/types';
import { IAccOpeningDetail, ILicenseDetail, ILegalDetail, IContactDetail } from '@core/main/types';
import { ELianlianCardStatus } from '../../types/lianlian';
import { IStepItem } from '@vue3/components';
import { IPersonalInfo } from '@core/main/src/components';
import { toRaw } from 'vue';

export function getFinanceLianAccOpeningParam(d: IFinanceLianAccOpening) {
  const licenseUrl = d.bankAccountOpenLicenseUrl || '';
  /** 手动填写支行信息 */
  const customWriteBranchBank = d.customStatus === 1;

  return {
    lianId: Number(d.lianId),

    bankType: d.bankType || undefined,

    bankAccountName: d.bankAccountName,
    bankAccountOpenLicenseUrl: licenseUrl,
    // @ts-ignore
    linkedPhone: d.phoneNo || d.linkedPhone,
    bankNo: d.bankNo,
    bankCode: d.bankCode,
    provinceCode: d.provinceCode,
    cityCode: d.cityCode,
    // @ts-ignore
    bankName: d.brabankName || d.bankName,
    creditCode: d.creditCode,

    /** 对公账户开户行号/大额行号 */
    cnapsCode: customWriteBranchBank ? d.cnapsCode : d.bankBranchNo,
    /** 开户支行名称 */
    bankBranch: customWriteBranchBank ? d.customBankBranchName : d.relationBranchBankName,
    /** 支行信息是否自定义(0:否,1:是) */
    customStatus: d.customStatus || 0,

    contactEmail: d.contactEmail,
    contactName: d.contactName,
    contactPhone: d.contactPhone,
  };
}

export function getMergeFinancePersonalLianInfo(agent: IAgentMergeInfo, detail = {} as IFinanceLianDetail): IFinanceLianDetail {
  const agentPerson = agent.agentPerson;

  if (agentPerson) {
    // 禁用字段
    detail.idNoFrontUrl = agentPerson?.idNoFront;
    detail.idNoBackUrl = agentPerson?.idNoBack;
    detail.name = agentPerson?.personName;
    detail.idType = EPayeeIdType.Ssn;
    detail.idNo = agentPerson?.idNo;

    // 非禁用字段
    detail.idNoExpiryStartDate = detail.idNoExpiryStartDate || agentPerson?.idNoExpiryStartDate;
    detail.idNoExpiryEndDate = detail.idNoExpiryEndDate || agentPerson?.idNoExpiryEndDate;
    detail.phoneNo = detail.phoneNo || agentPerson?.loginPhone;
  }

  detail.bankAccountNumber = detail.bankNo;
  detail.bankReservedPhone = detail.linkedPhone;

  return detail;
}

export function getMergeFinanceLianInfo(
  d: IAgentMergeInfo | IServerMergeInfo | ICompanyMergeInfo,
  detail = {} as IFinanceLianDetail,
  userType: EUserType,
): IFinanceLianDetail {
  const base = d[userType.toLowerCase() + 'Base'] as ICompany;
  const license = d[userType.toLowerCase() + 'Business'] as ILicenseDetail;
  const legal = d[userType.toLowerCase() + 'Legal'] as ILegalDetail;
  const contact = d[userType.toLowerCase() + 'Contacts'] as IContactDetail;
  const acc = d[userType.toLowerCase() + 'License'] as IAccOpeningDetail;

  const formType = base.type;
  const licenseType = acc.licenseType;
  const isLegalBank = formType === ECompanyType.Personal && licenseType === 0;

  detail.uboList = detail.uboList || [];

  if (license) {
    detail.businessLicensePic = license?.businessLicensePic;
    detail.name = license?.name;
    detail.address = license?.address;
    detail.creditCode = license?.creditCode;
    detail.legalName = license?.legalName;
    detail.businessLicenseStartDate = license?.businessLicenseStartDate;
    detail.businessLicenseEndDate = license?.businessLicenseEndDate;
  }

  if (legal) {
    detail.idNoFrontUrl = legal?.idNoFrontUrl;
    detail.idNoBackUrl = legal?.idNoBackUrl;
    detail.legalName = legal?.legalName;
    detail.legalPhone = legal?.legalPhone;
    detail.legalIdNo = legal?.legalIdNo;
    detail.idNoExpiryStartDate = legal?.idNoExpiryStartDate;
    detail.idNoExpiryEndDate = legal?.idNoExpiryEndDate;
  }

  if (acc) {
    detail.bankAccountOpenLicenseUrl = isLegalBank ? acc.bankCardUrl : acc?.licenseUrl;
    detail.bankAccountName = acc?.accountName;
    detail.creditCode = acc?.creditCode;
    detail.bankNo = isLegalBank ? acc.personBankAccount : acc?.bankAccount;
    detail.address = acc?.address;
    detail.linkedPhone = isLegalBank ? acc.bankPhone : acc?.phoneNo;
  }

  if (contact) {
    detail.contactName = detail.contactName || contact?.contactName;
    detail.contactPhone = detail.contactPhone || contact?.contactPhone;
    detail.contactEmail = detail.contactEmail || contact?.contactEmail;
  }

  detail.formType = formType;
  detail.idType = EPayeeIdType.Ssn;
  detail.licenseType = licenseType;

  return getMergeFinanceOpenedAccInfo(detail);
}

export function getMergeFinanceOpenedAccInfo(d: IFinanceLianDetail) {
  const detail = JSON.parse(JSON.stringify(d)) as IFinanceLianDetail;

  const { bankBranchNo, relationBranchBankName, customBankBranchName } = detail;

  const {
    bankCode, provinceCode, customStatus, cityCode,
    cnapsCode, bankBranch, bankNo, bankAccountName,
  } = detail || {};

  detail.bankAccountName = bankAccountName;
  detail.bankNo = bankNo;
  detail.bankCode = bankCode;
  detail.provinceCode = provinceCode;
  detail.cityCode = cityCode;
  detail.customStatus = customStatus || 0;

  if (detail.customStatus === 0) { // 自动匹配支行信息
    detail.cnapsCode = '';
    detail.customBankBranchName = '';
    detail.bankBranchNo = bankBranchNo || cnapsCode;
    detail.relationBranchBankName = relationBranchBankName || bankBranch;
  } else {
    detail.bankBranchNo = '';
    detail.relationBranchBankName = '';
    detail.cnapsCode = cnapsCode;
    detail.customBankBranchName = customBankBranchName || bankBranch;
  }

  return detail;
}

export function toLinalianStructure(info: IFinanceLianDetail): ILianDetail {
  const { userStatus, remark } = info;
  let s = ELianlianCardStatus.ACTIVATE_PENDING;

  switch (userStatus) {
    case EFinanceLianStatus.WAIT_CHECK:
      s = ELianlianCardStatus.ACTIVATE_PENDING;
      break;
    case EFinanceLianStatus.CHECK_PENDING:
      s = ELianlianCardStatus.CHECK_PENDING;
      break;
    case EFinanceLianStatus.ACTIVATE_PENDING_NEW:
      s = ELianlianCardStatus.ACTIVATE_PENDING_NEW;
      break;
    case EFinanceLianStatus.CHECK_REJECT:
      s = ELianlianCardStatus.CHECK_REJECT;
      break;
    case EFinanceLianStatus.NORMAL:
      s = ELianlianCardStatus.NORMAL;
      break;
    default:
      s = ELianlianCardStatus.ACTIVATE_PENDING;
      break;
  }

  return {
    ...toRaw(info),
    /** 账户状态 */
    accountStatus: s,
    remark,
  } as ILianDetail;
}

export function toPersonalCompStructure(d: IFinanceLianDetail) {
  const vals = toRaw(d) as IFinanceLianDetail;

  return {
    location: vals.address,
    occupation: vals.occupation,
    idFrontImageUrl: vals.idNoFrontUrl,
    idBackImageUrl: d.idNoBackUrl,
    userName: d.name,
    userPhone: d.phoneNo,
    idType: EPayeeIdType.Ssn,
    idNumber: d.idNo,
    idValidFrom: d.idNoExpiryStartDate,
    idValidTo: d.idNoExpiryEndDate,
  } as IPersonalInfo;
}

export function toFinanceLianPersonalStructure(d: IPersonalInfo) {
  return {
    idNoFrontUrl: d.idFrontImageUrl,
    idNoBackUrl: d.idBackImageUrl,
    idNoExpiryStartDate: d.idValidFrom,
    idNoExpiryEndDate: d.idValidTo,
    name: d.userName,
    idType: EPayeeIdType.Ssn,
    idNo: d.idNumber,
    phoneNo: d.userPhone,
    address: d.location,
    occupation: d.occupation,
  } as IFinanceLianPersonal;
}

export function toBindBankStructure(d: IFinanceLianDetail) {
  return {
    bankAccountNumber: d.bankNo,
    bankReservedPhone: d.linkedPhone,
  } as IPersonalInfo;
}

/**
 * 获取分步steps
 * @description 公共函数，很多页面公用，如果要根据场景进行修改，请增加参数适配
 */
export function getFinancePersonalLianSteps(): IStepItem<ELianLianStepKey>[] {
  return [
    {
      title: '上传开户基本信息',
      key: ELianLianStepKey.License,
    },
    {
      title: '开户绑卡信息',
      key: ELianLianStepKey.AccOpening,
    },
    {
      title: '支付信息认证',
      key: ELianLianStepKey.Submit,
    },
  ].filter(Boolean);
}

export function getFinancePersonalPrevLianStep(current: ELianLianStepKey): IStepItem<ELianLianStepKey> | undefined {
  const steps = getFinancePersonalLianSteps();
  const index = steps.findIndex((item) => item.key === current);

  return steps[index - 1];
}

export function getFinancePersonalLianStep(current: ELianLianStepKey): IStepItem<ELianLianStepKey> | undefined {
  const steps = getFinancePersonalLianSteps();

  return steps.find((item) => item.key === current);
}
